// =====================================================================
// src/lib/exportar.js — PDF e planilha, no padrão do Monteiro de Moraes
//
// Cópia fiel de `exportarHospedesPDF` / `exportarHospedesExcel` do app
// Gestao Flats Monteiro de Moraes: jsPDF + autoTable para o PDF, SheetJS
// para o .xlsx, as duas servidas de `/vendor/` pelo próprio site.
//
// O que mudou, e só isto:
//   - a logo do canto é a do produto (Smart Gestão Hoteleira), não a do
//     Monteiro. Decisão do Hugo em 16/09: uma logo só por ora; o hotel
//     aparece pelo NOME no título. Quando cada cliente tiver logo própria,
//     é só passar `logo` nas opções — o resto não muda.
//   - o nome do arquivo leva o slug do hotel, porque aqui há vários.
//
// As bibliotecas entram por <script defer>, então podem não estar prontas
// no primeiro instante da tela. Quem chama pergunta antes com `temPdf()` /
// `temPlanilha()` e some com o botão em vez de oferecer o que não funciona.
// =====================================================================

export const temPdf = () => !!(globalThis.jspdf && globalThis.jspdf.jsPDF);
export const temPlanilha = () => !!globalThis.XLSX;

const AZUL = [27, 58, 122];          // #1B3A7A, o azul dele
const CINZA_ALTERNADO = [248, 250, 252];

const limpo = s => String(s == null ? '' : s).normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();

// "auditoria-garvey-1104-2026-09-16.pdf"
export function nomeDoArquivo(base, partes, extensao) {
  return [limpo(base), ...(partes || []).map(limpo).filter(Boolean)].join('-') + '.' + extensao;
}

// A logo no canto superior direito, igual ao `_addLogoPDF` dele: 30×18mm,
// 4mm da borda. Sem logo, não desenha nada e não reclama.
function porLogo(doc, logo) {
  if (!logo) return;
  try {
    const w = 30, h = 18, y = 5, margem = 4;
    doc.addImage(logo, 'PNG', doc.internal.pageSize.getWidth() - w - margem, y, w, h);
  } catch (e) { console.warn('logo no PDF:', e); }
}

/**
 * Uma tabela em PDF, no desenho dele: título em azul, subtítulo cinza com
 * a data e a contagem, cabeçalho azul e linhas alternadas.
 *
 * @param {object} o
 * @param {string} o.titulo      — "Auditoria do apartamento 1104"
 * @param {string} o.hotel       — o nome do hotel, que entra no título
 * @param {string} [o.subtitulo] — se ausente, sai "Gerado em … · N linha(s)"
 * @param {string[]} o.colunas
 * @param {Array[]} o.linhas
 * @param {string} o.arquivo
 * @param {string} [o.logo]      — data URL PNG
 * @param {'portrait'|'landscape'} [o.orientacao]
 * @param {Array}  [o.rodape]    — a linha de rodapé (o TOTAL verde do `gerarRelatorioReservasPDF`, 4746)
 */
export function paraPdf(o) {
  if (!temPdf()) throw new Error('A biblioteca de PDF não carregou. Recarregue a página e tente de novo.');
  const { jsPDF } = globalThis.jspdf;
  const doc = new jsPDF({ orientation: o.orientacao || 'landscape', unit: 'mm', format: 'a4' });
  porLogo(doc, o.logo);
  doc.setFontSize(16); doc.setTextColor(...AZUL);
  doc.text(`${o.titulo} — ${o.hotel}`, 14, 16);
  doc.setFontSize(10); doc.setTextColor(107, 114, 128);
  const n = (o.linhas || []).length;
  doc.text(o.subtitulo || `Gerado em ${new Date().toLocaleDateString('pt-BR')} · ${n} linha${n === 1 ? '' : 's'}`, 14, 23);
  doc.autoTable({
    startY: 28,
    head: [o.colunas],
    body: (o.linhas || []).map(l => l.map(c => (c == null ? '' : String(c)))),
    ...(o.rodape ? { foot: [o.rodape.map(c => (c == null ? '' : String(c)))], footStyles: { fillColor: [5, 150, 105], textColor: 255, fontStyle: 'bold', fontSize: 9 } } : {}),
    headStyles: { fillColor: AZUL, fontSize: 10 },
    bodyStyles: { fontSize: 9 },
    alternateRowStyles: { fillColor: CINZA_ALTERNADO },
  });
  doc.save(o.arquivo);
}

/**
 * Uma aba de planilha .xlsx, como o `exportarHospedesExcel` dele — com a
 * largura de cada coluna, que é o que separa uma planilha de um CSV.
 *
 * @param {object} o
 * @param {string} o.aba
 * @param {string[]} o.colunas
 * @param {Array[]} o.linhas
 * @param {number[]} [o.larguras] — em caracteres; sem isto, calcula pelo conteúdo
 * @param {string} o.arquivo
 * @param {Array}  [o.rodape]   — linha em branco + a linha de total, como o `gerarRelatorioReservasExcel` (4667)
 */
export function paraPlanilha(o) {
  if (!temPlanilha()) throw new Error('A biblioteca de planilha não carregou. Recarregue a página e tente de novo.');
  const X = globalThis.XLSX;
  const dados = [o.colunas, ...(o.linhas || []).map(l => l.map(c => (c == null ? '' : c)))];
  if (o.rodape) { dados.push([]); dados.push(o.rodape.map(c => (c == null ? '' : c))); }
  const ws = X.utils.aoa_to_sheet(dados);
  ws['!cols'] = o.larguras ? o.larguras.map(w => ({ wch: w })) : larguraPeloConteudo(dados);
  const wb = X.utils.book_new();
  // 31 caracteres é o limite do Excel para nome de aba, e ele recusa : \ / ? * [ ]
  X.utils.book_append_sheet(wb, ws, String(o.aba || 'Dados').replace(/[:\\/?*[\]]/g, ' ').slice(0, 31));
  X.writeFile(wb, o.arquivo);
}

export function larguraPeloConteudo(dados) {
  const n = Math.max(...dados.map(l => l.length));
  const cols = [];
  for (let c = 0; c < n; c++) {
    let maior = 8;
    for (const l of dados) maior = Math.max(maior, String(l[c] == null ? '' : l[c]).length + 2);
    cols.push({ wch: Math.min(maior, 50) });
  }
  return cols;
}

/**
 * O extrato do proprietário em PDF (`_exportarRelatorioFinPropPDF`, 24666):
 * retrato; título azul, subtítulo cinza; "Resumo do Período" em linhas de
 * texto (as em negrito, coloridas pelo sinal); a tabela por flat; e o
 * rodapé "Critérios de cálculo" com quebra de linha, na base da última
 * página (nova página se não couber).
 *
 * @param {object} o
 * @param {string} o.titulo, o.hotel, o.subtitulo
 * @param {Array<{texto, negrito?, cor?: 'azul'|'vermelho'}>} o.resumo
 * @param {string[]} o.colunas  @param {Array[]} o.linhas
 * @param {string[]} o.criterios — a primeira linha é o título
 * @param {string} o.arquivo  @param {string} [o.logo]
 */
export function paraPdfExtrato(o) {
  if (!temPdf()) throw new Error('A biblioteca de PDF não carregou. Recarregue a página e tente de novo.');
  const { jsPDF } = globalThis.jspdf;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  porLogo(doc, o.logo);
  const pageW = doc.internal.pageSize.getWidth(), pageH = doc.internal.pageSize.getHeight();
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(...AZUL);
  doc.text(`${o.titulo} — ${o.hotel}`, 14, 18);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(100, 100, 100);
  doc.text(o.subtitulo || '', 14, 25);
  let y = 36;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...AZUL);
  doc.text('Resumo do Período', 14, y); y += 7;
  doc.setFontSize(11);
  for (const l of o.resumo || []) {
    doc.setFont('helvetica', l.negrito ? 'bold' : 'normal');
    if (l.cor === 'vermelho') doc.setTextColor(220, 38, 38); else if (l.cor === 'azul') doc.setTextColor(...AZUL); else doc.setTextColor(40, 40, 40);
    doc.text(l.texto, 14, y); y += l.negrito ? 7 : 6;
  }
  y += 3;
  if ((o.linhas || []).length) {
    doc.autoTable({
      head: [o.colunas], body: o.linhas.map(l => l.map(c => (c == null ? '' : String(c)))), startY: y,
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: AZUL, textColor: 255, fontStyle: 'bold', halign: 'center' },
      columnStyles: { 0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
      alternateRowStyles: { fillColor: [249, 250, 251] },
    });
    y = doc.lastAutoTable.finalY + 8;
  }
  // o rodapé com as fórmulas, sempre na base da última página
  const criterios = o.criterios || [];
  if (criterios.length) {
    const maxW = pageW - 28, lineH = 3.6;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    const corpo = criterios.slice(1).flatMap(t => doc.splitTextToSize(t, maxW));
    const footerH = lineH * (corpo.length + 1) + 6;
    if (y + footerH > pageH - 10) { doc.addPage(); porLogo(doc, o.logo); }
    const startY = pageH - footerH;
    doc.setDrawColor(200, 210, 225); doc.setLineWidth(0.3); doc.line(14, startY - 3, pageW - 14, startY - 3);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...AZUL);
    doc.text(criterios[0], 14, startY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(80, 80, 80);
    corpo.forEach((t, k) => doc.text(t, 14, startY + lineH * (k + 1)));
  }
  doc.save(o.arquivo);
}

/**
 * Uma planilha com VÁRIAS abas (`_exportarRelatorioFinPropExcel`, 24766).
 * @param {object} o
 * @param {Array<{nome, dados: Array[], larguras?: number[]}>} o.abas
 * @param {string} o.arquivo
 */
export function paraPlanilhaAbas(o) {
  if (!temPlanilha()) throw new Error('A biblioteca de planilha não carregou. Recarregue a página e tente de novo.');
  const X = globalThis.XLSX;
  const wb = X.utils.book_new();
  for (const aba of o.abas || []) {
    const dados = (aba.dados || []).map(l => l.map(c => (c == null ? '' : c)));
    const ws = X.utils.aoa_to_sheet(dados);
    ws['!cols'] = aba.larguras ? aba.larguras.map(w => ({ wch: w })) : larguraPeloConteudo(dados);
    X.utils.book_append_sheet(wb, ws, String(aba.nome || 'Dados').replace(/[:\\/?*[\]]/g, ' ').slice(0, 31));
  }
  X.writeFile(wb, o.arquivo);
}

/**
 * O "Relatório de Gestão" dos Gráficos (`exportPDF`, 18081). O MdM abre
 * uma janela de impressão com o HTML; aqui é o mesmo conteúdo em jsPDF —
 * título, a faixa do filtro, os três totais, a tabela "Dados Mensais" e
 * cada gráfico como imagem do canvas — porque um arquivo baixado é o que
 * a prova consegue afirmar e o que o usuário consegue mandar.
 *
 * @param {object} o
 * @param {string} o.titulo, o.hotel, o.filtro   — a linha "Proprietário · Período · Flats"
 * @param {string} [o.tituloTabela]             — "Dados Mensais" por padrão (o Ranking manda "Ranking")
 * @param {Array<{rotulo, valor, cor?}>} o.totais
 * @param {string} o.reservas                    — "Total de reservas no período: N"
 * @param {string[]} o.colunas  @param {Array[]} o.linhas
 * @param {Array<{titulo, imagem}>} o.graficos    — imagem = data URL PNG do canvas
 * @param {string} o.arquivo  @param {string} [o.logo]
 */
export function paraPdfGraficos(o) {
  if (!temPdf()) throw new Error('A biblioteca de PDF não carregou. Recarregue a página e tente de novo.');
  const { jsPDF } = globalThis.jspdf;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  porLogo(doc, o.logo);
  const pageW = doc.internal.pageSize.getWidth(), pageH = doc.internal.pageSize.getHeight();
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(...AZUL);
  doc.text(o.titulo, 14, 18);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(107, 114, 128);
  doc.text(o.hotel, 14, 25);
  let y = 32;
  doc.setFillColor(239, 246, 255); doc.setDrawColor(191, 219, 254); doc.roundedRect(14, y, pageW - 28, 9, 2, 2, 'FD');
  doc.setFontSize(9); doc.setTextColor(30, 58, 138); doc.text(o.filtro, 17, y + 6); y += 14;
  // os três totais (18224)
  const w = (pageW - 28 - 8) / 3;
  (o.totais || []).forEach((t, i) => {
    const x = 14 + i * (w + 4);
    doc.setFillColor(240, 249, 255); doc.setDrawColor(186, 230, 253); doc.roundedRect(x, y, w, 16, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...(t.cor === 'vermelho' ? [220, 38, 38] : t.cor === 'verde' ? [5, 150, 105] : AZUL));
    doc.text(t.valor, x + 4, y + 7);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(107, 114, 128); doc.text(t.rotulo, x + 4, y + 13);
  });
  y += 21;
  doc.setFillColor(240, 253, 244); doc.setDrawColor(187, 247, 208); doc.roundedRect(14, y, pageW - 28, 9, 2, 2, 'FD');
  doc.setFontSize(9); doc.setTextColor(6, 95, 70); doc.text(o.reservas || '', 17, y + 6); y += 14;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(55, 65, 81); doc.text(o.tituloTabela || 'Dados Mensais', 14, y); y += 3;
  doc.autoTable({ startY: y, head: [o.colunas], body: (o.linhas || []).map(l => l.map(c => (c == null ? '' : String(c)))),
    styles: { font: 'helvetica', fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: AZUL, textColor: 255, fontStyle: 'bold' }, alternateRowStyles: { fillColor: [249, 250, 251] } });
  y = doc.lastAutoTable.finalY + 8;
  // os gráficos, dois por linha (18214), o largo sozinho
  const gs = (o.graficos || []).filter(g => g.imagem);
  if (gs.length) {
    if (y > pageH - 70) { doc.addPage(); porLogo(doc, o.logo); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(55, 65, 81); doc.text('Gráficos', 14, y); y += 5;
    const colW = (pageW - 28 - 6) / 2, h = colW * 0.5;
    let col = 0;
    for (const g of gs) {
      const largo = !!g.largo;
      if (largo && col === 1) { col = 0; y += h + 12; }
      if (y + h + 12 > pageH - 10) { doc.addPage(); porLogo(doc, o.logo); y = 20; col = 0; }
      const x = 14 + col * (colW + 6), gw = largo ? pageW - 28 : colW, gh = largo ? (pageW - 28) * 0.3 : h;
      doc.setDrawColor(229, 231, 235); doc.roundedRect(x, y, gw, gh + 10, 2, 2, 'D');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(55, 65, 81); doc.text(g.titulo, x + 3, y + 5);
      try { doc.addImage(g.imagem, 'PNG', x + 3, y + 8, gw - 6, gh - 1); } catch (e) { console.warn('gráfico no PDF:', e); }
      if (largo) { col = 0; y += gh + 14; } else if (col === 0) col = 1; else { col = 0; y += h + 14; }
    }
  }
  doc.save(o.arquivo);
}
